import { useState, type ReactNode } from 'react';
import { MODIFIER_REGISTRY, findModifierKey, type ModifierDef } from '../../../lib/effects/types';
import {
  DEFAULT_RATE,
  DEFAULT_RUST_CLASSES,
  DEFAULT_WORKOUTS,
  awardsDefaults,
  challengesDefaults,
  coreStatDefaults,
  governDefaults,
  milestoneDefaults,
  perkDefaults,
  skillDefaults,
  startDefaults,
  statAwardsDefaults,
  workoutWeightsDefaults,
} from '../../../lib/skills/defaults';
import {
  ACTIVITY_CATEGORIES,
  CORE_STAT_IDS,
  CORE_STAT_NAMES,
  PERK_FLAGS,
  SKILL_FAMILIES,
  SKILL_IDS,
  SKILL_IDS_BY_FAMILY,
  type AwardsDef,
  type ChallengesDef,
  type CoreStatDef,
  type GovernDef,
  type MilestoneDef,
  type PerkDef,
  type RustClassDef,
  type SkillDef,
  type SkillRulesDef,
  type StartDef,
  type StatAwardsDef,
  type WorkoutWeightsDef,
} from '../../../lib/skills/types';
import { INFO_TEXT_MAX, INFO_TEXT_MIN, skillRulesAsParsed } from '../../../lib/validate/skills';
import { issuesUnder, type FieldErrors, type Issue } from '../../../lib/validate';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  TextField,
  inputCls,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';

/*
 * Outta-Ten content forms (ADR 0066, CONTRACT §2): coreStats[], skills[], perks[] and skillRules. Every number is content; the ids,
 * families, flags and modifier keys are code (lib/skills/types.ts, MODIFIER_REGISTRY). The validator (lib/validate/skills.ts) is the
 * rule; the forms only make the shapes easy to edit.
 */

const opts = (list: readonly string[]) => list.map((x) => ({ value: x, label: x }));
/** Registry keys a stat or skill may govern or a milestone/perk may carry (level keys are effects-only). */
const NUMBER_KEYS = MODIFIER_REGISTRY.filter(
  (k) => !k.key.startsWith('stat.') && !k.key.startsWith('skill.'),
);
const KEY_OPTIONS = NUMBER_KEYS.map((k) => ({ value: k.key, label: `${k.key} (${k.op})` }));
const ownerLabel = (owner: string) => {
  const [kind, id] = owner.split(':');
  return kind === 'stat' ? `${(id ?? '').toUpperCase()} (stat)` : `${id ?? ''} (skill)`;
};
/** Every owner a perk can name: `stat:<id>` for the six stats, `skill:<id>` for every launch skill. */
const PERK_OWNERS = [
  ...CORE_STAT_IDS.map((s) => `stat:${s}`),
  ...SKILL_IDS.map((s) => `skill:${s}`),
];

/** Modifier rows (key from the registry, op follows the key, value). */
export function ModifierRows({
  label,
  value,
  onChange,
  issues,
}: {
  label: string;
  value: (ModifierDef | null)[] | null | undefined;
  onChange: (next: ModifierDef[]) => void;
  issues?: Issue[];
}) {
  const list = (value ?? []).filter((m): m is ModifierDef => m != null);
  return (
    <ListEditor<ModifierDef>
      label={label}
      what="modifier"
      items={list}
      issues={issues}
      onChange={onChange}
      make={() => ({ key: 'combat.meleeDamage', op: 'mult', value: 1.1 })}
      row={(m, j, setM) => (
        <div className="grid grid-cols-3 gap-1">
          <SelectInput
            label={`${label} ${j + 1} key`}
            value={m.key}
            options={KEY_OPTIONS}
            onChange={(k) => setM({ ...m, key: k ?? '', op: findModifierKey(k)?.op ?? m.op })}
          />
          <span className="self-center text-xs font-mono">{m.op}</span>
          <NumberInput
            label={`${label} ${j + 1} value`}
            step={0.05}
            value={m.value}
            onChange={(v) => setM({ ...m, value: v ?? 0 })}
          />
        </div>
      )}
    />
  );
}

/** `PerkFlags` as checkboxes (unknown flags already on the row stay and show as a note). */
export function FlagChecks({
  label,
  value,
  onChange,
  issues,
}: {
  label: string;
  value: string[] | null | undefined;
  onChange: (next: string[]) => void;
  issues?: Issue[];
}) {
  const list = value ?? [];
  const [open, setOpen] = useState(list.length > 0);
  const unknown = list.filter((f) => !(PERK_FLAGS as readonly string[]).includes(f));
  return (
    <div className="sm:col-span-2">
      <button
        type="button"
        className="text-xs font-semibold uppercase tracking-wide"
        onClick={() => setOpen(!open)}
      >
        {label} ({list.length}) {open ? '-' : '+'}
      </button>
      {open && (
        <div className="mt-1 flex flex-wrap gap-3">
          {PERK_FLAGS.map((f) => (
            <label key={f} className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                aria-label={`${label} ${f}`}
                checked={list.includes(f)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...list, f] : list.filter((x) => x !== f))
                }
              />
              {f}
            </label>
          ))}
        </div>
      )}
      {unknown.length > 0 && (
        <p className="mt-0.5 text-xs text-blam">Not a flag: {unknown.join(', ')}</p>
      )}
      <IssueList issues={issues} />
    </div>
  );
}

/** A comma-separated list of numbers (an 11-value governs table, a rate curve, a rust perDay). Blank = empty. */
export function NumberListInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number[] | null | undefined;
  onChange: (next: number[]) => void;
}) {
  const shown = (value ?? []).join(', ');
  const [text, setText] = useState(shown);
  const [prev, setPrev] = useState(shown);
  if (prev !== shown) {
    setPrev(shown);
    setText(shown);
  }
  return (
    <input
      aria-label={label}
      className={`${inputCls} font-mono`}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const parts = e.target.value
          .split(',')
          .map((x) => x.trim())
          .filter((x) => x !== '');
        const nums = parts.map(Number);
        if (nums.every((n) => Number.isFinite(n))) onChange(nums);
      }}
    />
  );
}

function GovernsEditor({
  value,
  onChange,
  errors,
}: {
  value: (GovernDef | null)[] | null | undefined;
  onChange: (next: GovernDef[]) => void;
  errors: FieldErrors;
}) {
  const list = (value ?? []).filter((g): g is GovernDef => g != null);
  return (
    <ListEditor<GovernDef>
      label="Governs"
      what="key"
      hint="One contribution per key: neutral op perLevel x (level - pivot), or table[level] (11 values, the pivot entry = the key's neutral)."
      items={list}
      issues={errors.governs}
      onChange={onChange}
      make={() => governDefaults()}
      row={(g, j, setG) => (
        <div className="grid gap-1 sm:grid-cols-3">
          <SelectInput
            label={`Governs ${j + 1} key`}
            value={g.key}
            options={KEY_OPTIONS}
            onChange={(k) => setG({ ...g, key: k ?? '' })}
          />
          <NumberInput
            label={`Governs ${j + 1} per level`}
            step={0.01}
            value={g.perLevel}
            onChange={(v) => setG({ ...g, perLevel: v ?? 0 })}
          />
          <NumberListInput
            label={`Governs ${j + 1} table`}
            value={g.table}
            onChange={(table) => setG({ ...g, table })}
          />
          <div className="sm:col-span-3">
            <IssueList issues={errors[`governs.${j}`]} />
          </div>
        </div>
      )}
    />
  );
}

function MilestoneEditor({
  legend,
  hint,
  value,
  onChange,
  issues,
}: {
  legend: string;
  hint: string;
  value: MilestoneDef | null | undefined;
  onChange: (next: MilestoneDef | null) => void;
  issues?: Issue[];
}) {
  if (value == null)
    return (
      <Fieldset legend={legend}>
        <p className="text-xs text-bruise">{hint}</p>
        <button
          type="button"
          className="text-sm underline"
          onClick={() => onChange(milestoneDefaults())}
        >
          Add {legend.toLowerCase()}
        </button>
        <IssueList issues={issues} />
      </Fieldset>
    );
  const m = { ...milestoneDefaults(), ...value };
  return (
    <Fieldset legend={legend}>
      <p className="text-xs text-bruise sm:col-span-2">{hint}</p>
      <TextField
        label={`${legend} name`}
        value={m.name}
        onChange={(v) => onChange({ ...m, name: v })}
      />
      <TextField
        label={`${legend} text`}
        value={m.text}
        onChange={(v) => onChange({ ...m, text: v })}
      />
      <ModifierRows
        label={`${legend} modifiers`}
        value={m.modifiers}
        onChange={(modifiers) => onChange({ ...m, modifiers })}
      />
      <FlagChecks
        label={`${legend} flags`}
        value={m.flags}
        onChange={(flags) => onChange({ ...m, flags })}
      />
      <button type="button" className="text-sm underline" onClick={() => onChange(null)}>
        Remove {legend.toLowerCase()}
      </button>
      <div className="sm:col-span-2">
        <IssueList issues={issues} />
      </div>
    </Fieldset>
  );
}

/** ability7 (CONTRACT §13.4): the one perk this row owns that arrives at 7, no choice (from the draft's perks[] with that owner). */
function Ability7Editor({
  owner,
  value,
  onChange,
  issues,
  pivot,
}: {
  owner: string;
  value: string | null | undefined;
  onChange: (next: string) => void;
  issues?: Issue[];
  pivot: number;
}) {
  const b = useContentBundle();
  const mine = (b.perks ?? [])
    .filter((p) => p != null && p.owner === owner)
    .map((p) => p!.id ?? '');
  return (
    <Fieldset legend="Perk at 7">
      <p className="text-xs text-bruise sm:col-span-2">
        Arrives at level 7 like the ability at 5 and the signature at 10, and is active only while
        the level stays at 7 or more. No choice: one perk owned by {owner}, named by no other row.
        {pivot >= 7
          ? ' Live on a baseline sheet: the perk may carry flags and text, no modifiers.'
          : ''}
      </p>
      <SelectField
        label="Perk at 7"
        value={value ?? ''}
        none="(none)"
        options={opts(mine)}
        onChange={(v) => onChange(v ?? '')}
      />
      <div className="sm:col-span-2">
        <IssueList issues={issues} />
      </div>
    </Fieldset>
  );
}

/** affectsText / trainText (CONTRACT §13.2): the player's expandable info row, with a live length hint. */
function InfoFields<T extends CoreStatDef | SkillDef>({
  value: s,
  set,
  errors,
}: {
  value: T;
  set: (patch: Partial<T>) => void;
  errors: FieldErrors;
}) {
  const hint = (what: string, t: string | null | undefined) =>
    `${what} One or two short sentences, ${INFO_TEXT_MIN}..${INFO_TEXT_MAX} characters (now ${(t ?? '').length}). Don't name BRAWN, AIM or CARE.`;
  return (
    <Fieldset legend="Info row (profile)">
      <TextField
        label="What it affects"
        multiline
        hint={hint('What the player reads under "affects".', s.affectsText)}
        value={s.affectsText ?? ''}
        issues={errors.affectsText}
        onChange={(v) => set({ affectsText: v } as Partial<T>)}
      />
      <TextField
        label="How to raise it"
        multiline
        hint={hint('What the player reads under "how to raise it".', s.trainText)}
        value={s.trainText ?? ''}
        issues={errors.trainText}
        onChange={(v) => set({ trainText: v } as Partial<T>)}
      />
    </Fieldset>
  );
}

/** The parts every row shares: learning, rust, governs, the 5 / 7 / 10 milestones, the info row. */
function RowFields<T extends CoreStatDef | SkillDef>({
  owner,
  value: s,
  set,
  errors,
  pivot,
}: {
  owner: string;
  value: T;
  set: (patch: Partial<T>) => void;
  errors: FieldErrors;
  pivot: number;
}) {
  const b = useContentBundle();
  const rules = skillRulesAsParsed(b.skillRules ?? {});
  const classes =
    rules.rustClasses && Object.keys(rules.rustClasses).length > 0
      ? Object.keys(rules.rustClasses)
      : Object.keys(DEFAULT_RUST_CLASSES);
  return (
    <>
      <Fieldset legend="Learning and rust">
        <NumberField
          label="Learn speed"
          hint="0.1..3: the award multiplier for this row (skills ~1, stats ~0.3)."
          step={0.05}
          value={s.learnSpeed}
          issues={errors.learnSpeed}
          onChange={(v) => set({ learnSpeed: v ?? 0 } as Partial<T>)}
        />
        <SelectField
          label="Rust class"
          hint="skillRules.rustClasses: how fast it fades when unused."
          value={s.rustClass}
          options={opts(classes)}
          issues={errors.rustClass}
          onChange={(v) => set({ rustClass: v ?? '' } as Partial<T>)}
        />
      </Fieldset>
      <Fieldset legend="Governs">
        <p className="text-xs text-bruise sm:col-span-2">Pivot: level {pivot} plays like today.</p>
        <GovernsEditor
          value={s.governs}
          errors={errors}
          onChange={(governs) => set({ governs } as Partial<T>)}
        />
      </Fieldset>
      <MilestoneEditor
        legend="Ability at 5"
        hint={
          pivot >= 5
            ? 'Live on a baseline sheet: flags and text only, no modifiers.'
            : 'Arrives at level 5.'
        }
        value={s.ability5}
        issues={issuesUnder(errors, 'ability5')}
        onChange={(ability5) => set({ ability5 } as Partial<T>)}
      />
      <Ability7Editor
        owner={owner}
        pivot={pivot}
        value={s.ability7}
        issues={issuesUnder(errors, 'ability7')}
        onChange={(ability7) => set({ ability7 } as Partial<T>)}
      />
      <MilestoneEditor
        legend="Signature at 10"
        hint="Arrives at level 10."
        value={s.signature10}
        issues={issuesUnder(errors, 'signature10')}
        onChange={(signature10) => set({ signature10 } as Partial<T>)}
      />
      <InfoFields value={s} set={set} errors={errors} />
    </>
  );
}

/** One of the six core stats (`coreStats[]`). The id is code: STR, END, REF, WIT, NRV, CHA. */
export function CoreStatForm({ value, onChange, errors }: FormProps<CoreStatDef>) {
  const s = { ...coreStatDefaults(), ...value };
  const set = (patch: Partial<CoreStatDef>) => onChange({ ...s, ...patch });
  return (
    <>
      <Fieldset legend="Core stat">
        <SelectField
          label="Id"
          value={s.id}
          options={CORE_STAT_IDS.map((x) => ({ value: x, label: `${x} (${CORE_STAT_NAMES[x]})` }))}
          issues={errors.id}
          onChange={(v) => set({ id: v ?? '' })}
        />
        <TextField
          label="Name"
          value={s.name}
          issues={errors.name}
          onChange={(v) => set({ name: v })}
        />
        <TextField
          label="Short"
          mono
          value={s.short}
          issues={errors.short}
          onChange={(v) => set({ short: v })}
        />
        <TextField label="Text" value={s.text} onChange={(v) => set({ text: v })} />
      </Fieldset>
      <RowFields owner={`stat:${s.id ?? ''}`} value={s} set={set} errors={errors} pivot={5} />
    </>
  );
}

/** One skill (`skills[]`): weapon, field or home; its baseline is the pivot that plays like today. */
export function SkillForm({ value, onChange, errors }: FormProps<SkillDef>) {
  const s = { ...skillDefaults(), ...value };
  const set = (patch: Partial<SkillDef>) => onChange({ ...s, ...patch });
  const family = (SKILL_FAMILIES as readonly string[]).includes(s.family ?? '')
    ? (s.family as (typeof SKILL_FAMILIES)[number])
    : null;
  return (
    <>
      <Fieldset legend="Skill">
        <SelectField
          label="Id"
          value={s.id}
          options={opts(family ? SKILL_IDS_BY_FAMILY[family] : SKILL_IDS)}
          issues={errors.id}
          onChange={(v) => set({ id: v ?? '' })}
        />
        <SelectField
          label="Family"
          value={s.family}
          options={opts(SKILL_FAMILIES)}
          issues={errors.family}
          onChange={(v) => set({ family: v ?? '' })}
        />
        <TextField
          label="Name"
          value={s.name}
          issues={errors.name}
          onChange={(v) => set({ name: v })}
        />
        <TextField label="Text" value={s.text} onChange={(v) => set({ text: v })} />
        <NumberField
          label="Baseline"
          hint="0..10: the level that plays exactly like today (the pivot)."
          integer
          min={0}
          max={10}
          value={s.baseline}
          issues={errors.baseline}
          onChange={(v) => set({ baseline: v ?? 0 })}
        />
        <NumberField
          label="Can't below"
          hint="0..10: rust never takes the level under this."
          integer
          min={0}
          max={10}
          value={s.cantBelow}
          issues={errors.cantBelow}
          onChange={(v) => set({ cantBelow: v ?? 0 })}
        />
      </Fieldset>
      <RowFields
        owner={`skill:${s.id ?? ''}`}
        value={s}
        set={set}
        errors={errors}
        pivot={s.baseline ?? 0}
      />
    </>
  );
}

/** A perk (`perks[]`): a definition; the owner row's `ability7` names the one that arrives at 7 (CONTRACT §13.4). */
export function PerkForm({ value, onChange, errors }: FormProps<PerkDef>) {
  const p = { ...perkDefaults(), ...value };
  const set = (patch: Partial<PerkDef>) => onChange({ ...p, ...patch });
  return (
    <Fieldset legend="Perk">
      <TextField label="Id" mono value={p.id} issues={errors.id} onChange={(v) => set({ id: v })} />
      <SelectField
        label="Owner"
        hint="The stat or skill that owns it; that row's ability7 makes it arrive at 7 (an unnamed perk does nothing)."
        value={p.owner}
        options={PERK_OWNERS.map((o) => ({ value: o, label: ownerLabel(o) }))}
        issues={errors.owner}
        onChange={(v) => set({ owner: v ?? '' })}
      />
      <TextField
        label="Name"
        value={p.name}
        issues={errors.name}
        onChange={(v) => set({ name: v })}
      />
      <TextField label="Text" multiline value={p.text} onChange={(v) => set({ text: v })} />
      <ModifierRows
        label="Modifiers"
        value={p.modifiers}
        issues={errors.modifiers}
        onChange={(modifiers) => set({ modifiers })}
      />
      <FlagChecks
        label="Flags"
        value={p.flags}
        issues={errors.flags}
        onChange={(flags) => set({ flags })}
      />
    </Fieldset>
  );
}

type Tab = 'learning' | 'rust' | 'awards' | 'workouts' | 'other';
const TABS: [Tab, string][] = [
  ['learning', 'Learning'],
  ['rust', 'Rust'],
  ['awards', 'Awards'],
  ['workouts', 'Workouts'],
  ['other', 'Start and limits'],
];

function Tabs({ render }: { render: (tab: Tab) => ReactNode }) {
  const [tab, setTab] = useState<Tab>('learning');
  return (
    <>
      <div className="mb-3 flex flex-wrap gap-1" role="tablist">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`rounded border-2 px-3 py-1 text-sm font-semibold ${
              tab === k ? 'border-ink bg-caption' : 'border-newsprint'
            }`}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>
      {render(tab)}
    </>
  );
}

/** Category -> weight per credited minute; blank removes it. Empty map = the C# defaults (shown as placeholders). */
function WeightMap({
  label,
  value,
  fallback,
  onChange,
  issues,
}: {
  label: string;
  value: Record<string, number> | null | undefined;
  fallback: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
  issues?: Issue[];
}) {
  const map = value ?? {};
  const empty = Object.keys(map).length === 0;
  return (
    <div className="sm:col-span-2">
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      {empty && <p className="text-xs text-bruise">Empty: the C# defaults apply (shown faded).</p>}
      <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-5">
        {ACTIVITY_CATEGORIES.map((c) => (
          <label key={c} className="text-xs font-semibold">
            {c}
            <NumberInput
              label={`${label} ${c}`}
              nullable
              step={0.1}
              min={0}
              max={10}
              placeholder={fallback[c] == null ? '' : String(fallback[c])}
              value={map[c]}
              onChange={(v) => {
                const next = { ...(empty ? fallback : map) };
                if (v == null) delete next[c];
                else next[c] = v;
                onChange(next);
              }}
            />
          </label>
        ))}
      </div>
      <IssueList issues={issues} />
    </div>
  );
}

function ConditionIds({
  label,
  hint,
  value,
  onChange,
  issues,
}: {
  label: string;
  hint: string;
  value: string[] | null | undefined;
  onChange: (next: string[]) => void;
  issues?: Issue[];
}) {
  const b = useContentBundle();
  const ids = (b.conditions?.types ?? []).filter((c) => c != null).map((c) => c!.id ?? '');
  const list = value ?? [];
  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      <div className="mt-1 flex flex-wrap gap-3">
        {ids.map((c) => (
          <label key={c} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              aria-label={`${label} ${c}`}
              checked={list.includes(c)}
              onChange={(e) =>
                onChange(e.target.checked ? [...list, c] : list.filter((x) => x !== c))
              }
            />
            {c}
          </label>
        ))}
      </div>
      <p className="mt-0.5 text-xs text-bruise">{hint}</p>
      <IssueList issues={issues} />
    </div>
  );
}

const AWARD_FIELDS: [keyof AwardsDef, string][] = [
  ['kill', 'Kill'],
  ['headshot', 'Headshot'],
  ['stealthKill', 'Stealth kill'],
  ['search', 'Search'],
  ['forcedDoor', 'Forced door'],
  ['lockPicked', 'Lock picked'],
  ['treat', 'Treat'],
  ['revive', 'Revive'],
  ['throwLanded', 'Throw landed'],
  ['bigWin', 'Big win'],
  ['homeBuild', 'Home build'],
  ['homeDischarge', 'Home discharge'],
];
const STAT_AWARD_FIELDS: [keyof StatAwardsDef, string][] = [
  ['meleeKillStr', 'STR per melee kill'],
  ['haulStr', 'STR for a heavy haul'],
  ['haulKgPerMember', 'Haul kg per member'],
  ['extractedEnd', 'END for extracting'],
  ['waveEnd', 'END per wave'],
  ['rangedKillRef', 'REF per ranged kill'],
  ['stealthKillRef', 'REF per stealth kill'],
  ['reviveRef', 'REF per revive'],
  ['searchWit', 'WIT per search'],
  ['buildWit', 'WIT per build'],
  ['waveNrv', 'NRV per wave'],
  ['bigWinNrv', 'NRV per big win'],
  ['revivedNrv', 'NRV when revived'],
  ['recruitCha', 'CHA per recruit'],
  ['tradeCha', 'CHA per trade'],
];
const CHALLENGE_FIELDS: [keyof ChallengesDef, string][] = [
  ['door', 'Door'],
  ['lock', 'Lock'],
  ['revive', 'Revive'],
  ['treat', 'Treat'],
  ['search', 'Search'],
];

/** `skillRules`: the learning curve, rust, awards, workouts, starting swaps and the limits. Stamina lives under Raid rules. */
export function SkillRulesForm({ value, onChange, errors }: FormProps<SkillRulesDef>) {
  const r = skillRulesAsParsed(value ?? {});
  const set = (patch: Partial<SkillRulesDef>) => onChange({ ...r, ...patch });
  const num = (
    k: keyof SkillRulesDef,
    label: string,
    hint: string,
    o: { integer?: boolean; step?: number } = {},
  ) => (
    <NumberField
      key={k}
      label={label}
      hint={hint}
      integer={o.integer}
      step={o.step ?? (o.integer ? undefined : 0.05)}
      value={r[k] as number | undefined}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v ?? 0 } as Partial<SkillRulesDef>)}
    />
  );
  const awards = { ...awardsDefaults(), ...r.awards };
  const stat = { ...statAwardsDefaults(), ...awards.stat };
  const challenges = { ...challengesDefaults(), ...r.challenges };
  const workouts: WorkoutWeightsDef = { ...workoutWeightsDefaults(), ...r.workouts };
  const start: StartDef = { ...startDefaults(), ...r.start };
  const rust = r.rustClasses ?? {};
  const rustShown: Record<string, RustClassDef | null> =
    Object.keys(rust).length > 0 ? rust : DEFAULT_RUST_CLASSES;
  return (
    <Tabs
      render={(tab) => {
        if (tab === 'learning')
          return (
            <>
              <Fieldset legend="Switch">
                <CheckboxField
                  label="Enabled"
                  hint="Off: nothing trains or rusts (the sheet stays as it is)."
                  checked={r.enabled !== false}
                  onChange={(on) => set({ enabled: on })}
                />
              </Fieldset>
              <Fieldset legend="Rate curve">
                <div className="sm:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wide">Rate</span>
                  <NumberListInput label="Rate" value={r.rate} onChange={(rate) => set({ rate })} />
                  <p className="text-xs text-bruise">
                    10 values, index = level 0..9 (the award multiplier at that level). Empty ={' '}
                    {DEFAULT_RATE.join(', ')}.
                  </p>
                  <IssueList issues={errors.rate} />
                </div>
              </Fieldset>
              <Fieldset legend="Caps and challenge">
                {num('dailyCapPoints', 'Daily cap points', '1..1000 per row per day.', {
                  integer: true,
                })}
                {num(
                  'homeDailyCapPoints',
                  'Home daily cap points',
                  '1..1000 per home skill per day.',
                  {
                    integer: true,
                  },
                )}
                {num(
                  'challengeFloor',
                  'Challenge floor',
                  '0..1: the share of an award an easy thing still gives.',
                )}
                {num(
                  'challengeStepPerLevel',
                  'Challenge step per level',
                  '0..1 per level of challenge above you.',
                )}
                {num(
                  'muscleMemoryMult',
                  'Muscle memory mult',
                  '1..10: learning back below your peak.',
                  {
                    step: 0.5,
                  },
                )}
                {num('homeSkillLearnPerWit', 'Home skill learn per WIT', '0..1 per WIT point.', {
                  step: 0.01,
                })}
                {num('devPointsMult', 'Dev points mult', '1..10; must be 1 in production.', {
                  integer: true,
                })}
              </Fieldset>
              <Fieldset legend="Learning while unwell">
                {num(
                  'reducedShare',
                  'Reduced share',
                  '0..1 of points while in a reduced condition.',
                )}
                <ConditionIds
                  label="Reduced conditions"
                  hint="Learning gives the reduced share."
                  value={r.reducedConditions}
                  issues={errors.reducedConditions}
                  onChange={(v) => set({ reducedConditions: v })}
                />
                <ConditionIds
                  label="No share conditions"
                  hint="Learning gives nothing."
                  value={r.noShareConditions}
                  issues={errors.noShareConditions}
                  onChange={(v) => set({ noShareConditions: v })}
                />
              </Fieldset>
            </>
          );
        if (tab === 'rust')
          return (
            <>
              <Fieldset legend="Rust timing">
                {num('graceDays', 'Grace days', '0..30 idle days before rust starts.', {
                  integer: true,
                })}
                {num(
                  'deathRustDays',
                  'Death rust days',
                  '0..60: a death rusts the sheet as if this many idle days passed.',
                  {
                    integer: true,
                  },
                )}
              </Fieldset>
              {Object.entries(rustShown).map(([id, c]) => (
                <Fieldset key={id} legend={`Rust class ${id}`}>
                  <div className="sm:col-span-2">
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      Points lost per day
                    </span>
                    <NumberListInput
                      label={`Rust ${id} perDay`}
                      value={c?.perDay}
                      onChange={(perDay) =>
                        set({ rustClasses: { ...rustShown, [id]: { ...c, perDay } } })
                      }
                    />
                    <p className="text-xs text-bruise">11 values, index = level 0..10.</p>
                  </div>
                  <NumberField
                    label={`Rust ${id} floor points`}
                    hint="0..1000: rust stops here."
                    integer
                    value={c?.floorPoints}
                    onChange={(v) =>
                      set({ rustClasses: { ...rustShown, [id]: { ...c, floorPoints: v ?? 0 } } })
                    }
                  />
                  <NumberField
                    label={`Rust ${id} below peak points`}
                    hint="0..1000: never more than this under the peak."
                    integer
                    value={c?.belowPeakPoints}
                    onChange={(v) =>
                      set({
                        rustClasses: { ...rustShown, [id]: { ...c, belowPeakPoints: v ?? 0 } },
                      })
                    }
                  />
                  <div className="sm:col-span-2">
                    <IssueList issues={issuesUnder(errors, `rustClasses.${id}`)} />
                  </div>
                </Fieldset>
              ))}
            </>
          );
        if (tab === 'awards')
          return (
            <>
              <Fieldset legend="Skill awards (points)">
                {AWARD_FIELDS.map(([k, label]) => (
                  <NumberField
                    key={k}
                    label={label}
                    integer
                    min={0}
                    value={awards[k] as number}
                    onChange={(v) => set({ awards: { ...awards, [k]: v ?? 0 } })}
                  />
                ))}
                <div className="sm:col-span-2">
                  <IssueList issues={errors.awards} />
                </div>
              </Fieldset>
              <Fieldset legend="Stat awards (points)">
                {STAT_AWARD_FIELDS.map(([k, label]) => (
                  <NumberField
                    key={k}
                    label={label}
                    integer={k !== 'haulKgPerMember'}
                    min={0}
                    value={stat[k]}
                    onChange={(v) => set({ awards: { ...awards, stat: { ...stat, [k]: v ?? 0 } } })}
                  />
                ))}
                <div className="sm:col-span-2">
                  <IssueList issues={issuesUnder(errors, 'awards.stat')} />
                </div>
              </Fieldset>
              <Fieldset legend="Challenges (1..10)">
                {CHALLENGE_FIELDS.map(([k, label]) => (
                  <NumberField
                    key={k}
                    label={`${label} challenge`}
                    integer
                    min={1}
                    max={10}
                    value={challenges[k]}
                    onChange={(v) => set({ challenges: { ...challenges, [k]: v ?? 0 } })}
                  />
                ))}
                <div className="sm:col-span-2">
                  <IssueList issues={errors.challenges} />
                </div>
              </Fieldset>
            </>
          );
        if (tab === 'workouts')
          return (
            <Fieldset legend="Workouts into the body">
              <WeightMap
                label="END per credited minute"
                value={workouts.endPerCreditedMin}
                fallback={DEFAULT_WORKOUTS.endPerCreditedMin}
                issues={errors['workouts.endPerCreditedMin']}
                onChange={(m) => set({ workouts: { ...workouts, endPerCreditedMin: m } })}
              />
              <WeightMap
                label="STR per credited minute"
                value={workouts.strPerCreditedMin}
                fallback={DEFAULT_WORKOUTS.strPerCreditedMin}
                issues={errors['workouts.strPerCreditedMin']}
                onChange={(m) => set({ workouts: { ...workouts, strPerCreditedMin: m } })}
              />
              <WeightMap
                label="Melee per credited minute"
                value={workouts.meleePerCreditedMin}
                fallback={DEFAULT_WORKOUTS.meleePerCreditedMin}
                issues={errors['workouts.meleePerCreditedMin']}
                onChange={(m) => set({ workouts: { ...workouts, meleePerCreditedMin: m } })}
              />
              <div className="sm:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide">Melee skills</span>
                <div className="mt-1 flex flex-wrap gap-3">
                  {SKILL_IDS_BY_FAMILY.weapon.map((id) => {
                    const list = workouts.meleeSkills ?? [];
                    return (
                      <label key={id} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          aria-label={`Melee skill ${id}`}
                          checked={list.includes(id)}
                          onChange={(e) =>
                            set({
                              workouts: {
                                ...workouts,
                                meleeSkills: e.target.checked
                                  ? [...list, id]
                                  : list.filter((x) => x !== id),
                              },
                            })
                          }
                        />
                        {id}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-bruise">
                  None ticked = {DEFAULT_WORKOUTS.meleeSkills.join(', ')}.
                </p>
                <IssueList issues={errors['workouts.meleeSkills']} />
              </div>
              <NumberField
                label="Melee max level"
                hint="0..10: workouts never take a melee skill past this."
                integer
                min={0}
                max={10}
                value={workouts.meleeMaxLevel}
                issues={errors['workouts.meleeMaxLevel']}
                onChange={(v) => set({ workouts: { ...workouts, meleeMaxLevel: v ?? 0 } })}
              />
            </Fieldset>
          );
        return (
          <>
            <Fieldset legend="Start">
              <NumberField
                label="Player swaps"
                hint="0..6 stat swaps in the character creator."
                integer
                value={start.playerSwaps}
                onChange={(v) => set({ start: { ...start, playerSwaps: v ?? 0 } })}
              />
              <NumberField
                label="Recruit swaps"
                integer
                value={start.recruitSwaps}
                onChange={(v) => set({ start: { ...start, recruitSwaps: v ?? 0 } })}
              />
              <NumberField
                label="Swap points"
                hint="0..500 points moved per swap."
                integer
                value={start.swapPoints}
                issues={errors['start.swapPoints']}
                onChange={(v) => set({ start: { ...start, swapPoints: v ?? 0 } })}
              />
              <div className="sm:col-span-2">
                <IssueList issues={errors.start} />
              </div>
            </Fieldset>
            <Fieldset legend="Limits">
              {num(
                'signatureHeadshot',
                'Signature headshot',
                '0..1: the headshot chance a 10 reaches.',
              )}
              {num('reloadMinS', 'Reload min s', '0..5: the floor under combat.reload.', {
                step: 0.05,
              })}
              {num('knockbackMaxM', 'Knockback max m', 'Above 0, at most 10.', { step: 0.1 })}
              {num('maxBonusLootRolls', 'Max bonus loot rolls', '0..5 (raid.lootRolls).', {
                integer: true,
              })}
            </Fieldset>
          </>
        );
      }}
    />
  );
}
